import { template as template_3b95e4ce5bf14b1e9511c91a452eaad6 } from "@ember/template-compiler";
import { buildInsuranceFacetOptions } from 'client-portal/utils/clinicianSearch/insurance-facet-options';
import { facetOptionDisabled } from 'client-portal/utils/clinicianSearch/facet-catalog';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { parseTrimmedString } from 'client-portal/utils/clinicianSearch/comma-query-param';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';
import RequestClinicianSearchFiltersFilterRadioLine from '../filters/filter-radio-line';
import RequestClinicianSearchFiltersPanel from '../filters/panel';
import styles from './insurance.module.scss';
import svgJar from 'ember-svg-jar/helpers/svg-jar';
import t from 'ember-intl/helpers/t';
const RADIO_NAME = 'clinician-search-insurance-filter';
const PROVIDERS_HEADING_ID = 'clinician-search-insurance-providers-heading';
export default class RequestClinicianSearchFacetFiltersInsurance extends Component {
    @service
    intl;
    @service
    clinicianSearch;
    @service
    currentPractice;
    radioName = RADIO_NAME;
    providersHeadingId = PROVIDERS_HEADING_ID;
    @tracked
    options = [];
    @tracked
    searchDraft = '';
    @tracked
    loadFailed = false;
    @tracked
    isLoadingOptions = true;
    get selectedInsurance() {
        return parseTrimmedString(this.clinicianSearch.query.insurance);
    }
    get panelLoading() {
        return this.isLoadingOptions || this.clinicianSearch.sendAll.isLoading;
    }
    get insuranceLoadingAriaLabel() {
        if (this.clinicianSearch.sendAll.isLoading) {
            return this.intl.t('booking.clinicianSearch.filterDetail.filterOptionsLoading');
        }
        return this.intl.t('booking.clinicianSearch.filterDetail.insuranceOptionsLoading');
    }
    get providersHeading() {
        return this.intl.t('booking.clinicianSearch.filterDetail.insuranceProvidersHeading', {
            practiceName: this.currentPractice.get('fullName')
        });
    }
    get selfPayOption() {
        let option = this.options.find((opt)=>opt.isSelfPay);
        return option ? {
            ...option,
            displayLabel: this.optionLabel(option)
        } : null;
    }
    get providerOptions() {
        let providers = this.options.filter((opt)=>!opt.isSelfPay);
        let needle = this.searchDraft.trim().toLowerCase();
        if (needle) {
            providers = providers.filter((opt)=>opt.label.toLowerCase().includes(needle));
        }
        return providers;
    }
    refreshOptions = ()=>{
        this.loadFailed = false;
        this.isLoadingOptions = true;
        try {
            let routeParams = {
                ...this.clinicianSearch.query.toRouteParams(),
                insurance: null
            };
            let meta = this.clinicianSearch.sendAll.buildFacetsMeta(routeParams);
            this.options = buildInsuranceFacetOptions(this.clinicianSearch.sendAll.records, meta?.facets?.insurances ?? []);
        } catch  {
            this.loadFailed = true;
            this.options = [];
        } finally{
            this.isLoadingOptions = false;
        }
    };
    setSearchDraft = (event)=>{
        this.searchDraft = event.target.value;
    };
    isOptionSelected = (opt)=>this.selectedInsurance.toLowerCase() === String(opt.key ?? '').toLowerCase();
    optionDisabled = (opt)=>facetOptionDisabled(opt.docCount, this.isOptionSelected(opt));
    insuranceOptionRowTitle = (opt)=>{
        if (!this.optionDisabled(opt)) {
            return '';
        }
        return this.intl.t('booking.clinicianSearch.filterDetail.disabledFilterOption');
    };
    optionLabel = (opt)=>{
        if (opt.isSelfPay) {
            return this.intl.t('booking.clinicianSearch.filterDetail.insuranceSelfPayOption');
        }
        return opt.label;
    };
    selectInsurance = (opt, event)=>{
        if (!event.target.checked || !opt.key) {
            return;
        }
        this.clinicianSearch.query.applyFacetFilter('insurance', opt.key);
        this.clinicianSearch.mobile.backFromDetail();
    };
    static{
        template_3b95e4ce5bf14b1e9511c91a452eaad6(`
    <RequestClinicianSearchFiltersPanel
      @hideApply={{true}}
      @class={{styles.component}}
      class="clinician-search-filter-insurance-options"
      @facetKey="insurance"
      @isLoading={{this.panelLoading}}
      @loadingAriaLabel={{this.insuranceLoadingAriaLabel}}
      @onLoad={{this.refreshOptions}}
      data-test-clinician-search-filter-insurance-options
    >
      <div class="insurance-header">
        <p class="filter-section-title" id="clinician-search-insurance-filter-title">{{t
            "booking.clinicianSearch.filterDetail.insuranceFilterPanelTitle"
          }}</p>
        <div class="insurance-search-row">
          <span class="insurance-search-icon">{{svgJar
              "therapist-name-search"
              aria-hidden="true"
            }}</span>
          <input
            class="insurance-search-input"
            type="search"
            autocomplete="off"
            aria-label={{t "booking.clinicianSearch.filterDetail.insuranceSearchPlaceholder"}}
            placeholder={{t "booking.clinicianSearch.filterDetail.insuranceSearchPlaceholder"}}
            value={{this.searchDraft}}
            data-test-clinician-search-filter-insurance-input
            {{on "input" this.setSearchDraft}}
          />
        </div>
      </div>
      {{#if this.loadFailed}}
        <p class="error" data-test-clinician-search-filter-insurance-options-error>
          {{t "booking.clinicianSearch.filterDetail.insuranceLoadError"}}
        </p>
      {{/if}}
      {{#if this.selfPayOption}}
        <ul
          class="filter-checkbox-list insurance-self-pay-list"
          role="list"
          aria-labelledby="clinician-search-insurance-filter-title"
        >
          <RequestClinicianSearchFiltersFilterRadioLine
            @kind="insurance"
            @optionId={{this.selfPayOption.slug}}
            @docCount={{this.selfPayOption.docCount}}
            @name={{this.radioName}}
            @value={{this.selfPayOption.key}}
            @checked={{this.isOptionSelected this.selfPayOption}}
            @disabled={{this.optionDisabled this.selfPayOption}}
            @title={{this.insuranceOptionRowTitle this.selfPayOption}}
            @onChange={{fn this.selectInsurance this.selfPayOption}}
          >
            {{this.selfPayOption.displayLabel}}
          </RequestClinicianSearchFiltersFilterRadioLine>
        </ul>
      {{/if}}
      <div class="body">
        {{#if this.providerOptions}}
          <div
            class="insurance-providers-block"
            role="group"
            aria-labelledby={{this.providersHeadingId}}
          >
            <p
              class="insurance-providers-heading"
              id={{this.providersHeadingId}}
              data-test-clinician-search-filter-insurance-providers-heading
            >{{this.providersHeading}}</p>
            <ul class="filter-checkbox-list" role="list">
              {{#each this.providerOptions as |opt|}}
                <RequestClinicianSearchFiltersFilterRadioLine
                  @kind="insurance"
                  @optionId={{opt.slug}}
                  @docCount={{opt.docCount}}
                  @name={{this.radioName}}
                  @value={{opt.key}}
                  @checked={{this.isOptionSelected opt}}
                  @disabled={{this.optionDisabled opt}}
                  @title={{this.insuranceOptionRowTitle opt}}
                  @onChange={{fn this.selectInsurance opt}}
                >
                  {{opt.label}}
                </RequestClinicianSearchFiltersFilterRadioLine>
              {{/each}}
            </ul>
          </div>
        {{else}}
          {{#unless this.loadFailed}}
            <p class="no-results" data-test-clinician-search-filter-insurance-no-results>
              {{t "booking.clinicianSearch.filterDetail.insuranceNoResults"}}
            </p>
          {{/unless}}
        {{/if}}
      </div>
    </RequestClinicianSearchFiltersPanel>
  `, {
            component: this,
            eval () {
                return eval(arguments[0]);
            }
        });
    }
}
